/**
 * Waveform peaks (DENG-905): the amplitude envelope a waveform is drawn from.
 *
 * paeapi generates them, one file per audio object, stored beside it as `<audio url>.peaks.json`:
 * on upload, and for anything older the first time someone views it. Clients never decode audio
 * to draw a waveform. They fetch the peaks file and, when it isn't there yet, ask the server for it
 * and poll until it appears (`resolveWaveformPeaks`). Shared by web and ns-myfender, so the format
 * and the waiting behaviour can't drift between them.
 */

/** Time resolution the server writes: 10ms per point, so a five-minute track is ~60KB. */
export const PEAKS_POINTS_PER_SECOND = 100;

export interface WaveformPeaks {
  /** How many min/max points cover one second of audio. */
  pointsPerSecond: number;
  /** Track length in seconds. */
  duration: number;
  /** Per-point troughs, -1..0. */
  min: Float32Array;
  /** Per-point crests, 0..1. */
  max: Float32Array;
}

/**
 * The over-the-wire shape (paeapi src/libraries/waveformPeaks.ts writes it). `data` is min,max
 * interleaved as signed integers: base64 at 8 bits (what the server writes, ~200 bytes per second
 * of audio) or a plain array.
 */
export interface PeaksFile {
  version: number;
  pointsPerSecond: number;
  duration: number;
  /** Bit depth of the integers in `data`. Defaults to 8. */
  bits?: number;
  data: string | number[];
}

const DEFAULT_BITS = 8;

/** Peaks sit beside the audio object, so the URL is derived rather than looked up. */
export function peaksUrlFor(audioUrl: string): string {
  const base = `${audioUrl}`.split("?")[0];
  return `${base}.peaks.json`;
}

/** Decodes a peaks file into typed arrays. Throws if it isn't one. */
export function parsePeaksFile(raw: any): WaveformPeaks {
  const file = raw as PeaksFile;
  if (!file || !file.data || !file.duration || !file.pointsPerSecond) {
    throw new Error("Not a peaks file");
  }
  const bits = file.bits || DEFAULT_BITS;
  const scale = Math.pow(2, bits - 1);
  const pairs = typeof file.data === "string" ? decodeSignedBase64(file.data, bits) : file.data;

  const points = Math.floor(pairs.length / 2);
  const min = new Float32Array(points);
  const max = new Float32Array(points);
  for (let i = 0; i < points; i++) {
    min[i] = Math.max(-1, pairs[i * 2] / scale);
    max[i] = Math.min(1, pairs[i * 2 + 1] / scale);
  }
  return { pointsPerSecond: file.pointsPerSecond, duration: file.duration, min, max };
}

// ---------- waiting for the server ----------

/**
 * How long to wait between looks for a file the server is generating. Measured on dev, a
 * first-view request takes about 5-15s end to end (queue pickup, a cold start, then 0.5-3s of
 * decoding), so the looks bunch up early and the whole schedule gives up after about a minute.
 */
export const PEAKS_POLL_DELAYS_MS = [1000, 2000, 2000, 3000, 3000, 5000, 5000, 5000, 5000, 10000, 10000, 10000];

/** What `POST content/file/{id}/peaks` answers. `null` when it couldn't be asked at all. */
export type PeaksRequestStatus = "ready" | "queued" | "unsupported" | null;

export interface ResolvePeaksOptions {
  /** One look at `<audio url>.peaks.json`: the peaks, or null while it doesn't exist. */
  fetchPeaks: () => Promise<WaveformPeaks | null>;
  /** Asks the server to generate the file. Must not throw: resolve null on any failure. */
  requestGeneration: () => Promise<PeaksRequestStatus>;
  /** Stops polling once the caller no longer wants the answer (screen closed, file switched). */
  isCancelled?: () => boolean;
  delaysMs?: number[];
  sleep?: (ms: number) => Promise<void>;
}

/** Why no peaks came back. Callers show a placeholder (mobile) or fall back to decoding (web). */
export class PeaksUnavailableError extends Error {
  constructor(public readonly reason: "unsupported" | "not-requested" | "timed-out" | "cancelled") {
    super(`Waveform peaks unavailable: ${reason}`);
    this.name = "PeaksUnavailableError";
  }
}

/**
 * Gets a file's peaks, asking the server to generate them if they don't exist yet and waiting
 * for them to appear.
 *
 * Gives up (PeaksUnavailableError) when the file isn't audio, when generation couldn't be
 * requested, or when the file still hasn't appeared at the end of the schedule, which is what
 * happens when ffmpeg can't decode the upload. The next view asks again.
 */
export async function resolveWaveformPeaks(options: ResolvePeaksOptions): Promise<WaveformPeaks> {
  const { fetchPeaks, requestGeneration, isCancelled = () => false, delaysMs = PEAKS_POLL_DELAYS_MS, sleep = defaultSleep } = options;

  const existing = await fetchPeaks();
  if (existing) return existing;
  if (isCancelled()) throw new PeaksUnavailableError("cancelled");

  const status = await requestGeneration();
  if (status === "unsupported") throw new PeaksUnavailableError("unsupported");
  if (!status) throw new PeaksUnavailableError("not-requested");

  // "ready" means the file landed between the first look and the request: look again at once.
  const delays = status === "ready" ? [0, ...delaysMs] : delaysMs;
  for (const delay of delays) {
    if (delay) await sleep(delay);
    if (isCancelled()) throw new PeaksUnavailableError("cancelled");
    const peaks = await fetchPeaks();
    if (peaks) return peaks;
  }
  throw new PeaksUnavailableError("timed-out");
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ---------- base64 of signed ints ----------

const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

/**
 * Always the plain-JS decoder, never `atob`. On iOS, NativeScript's `atob` is a native NSString
 * category, and its `btoa` sibling is already known to produce wrong output. Peaks are signed
 * bytes, so half of them are above 0x7F, exactly where a decoder that treats the result as text
 * mangles or rejects them. One decoder everywhere means web and mobile can't disagree, and a
 * peaks file is small enough (about 60KB for five minutes) that speed doesn't matter.
 */
function decodeSignedBase64(base64: string, bits: number): number[] {
  const binary = manualDecode(base64);
  const out: number[] = [];
  if (bits > 8) {
    for (let i = 0; i + 1 < binary.length; i += 2) {
      const raw = binary.charCodeAt(i) | (binary.charCodeAt(i + 1) << 8);
      out.push(raw > 0x7fff ? raw - 0x10000 : raw);
    }
  } else {
    for (let i = 0; i < binary.length; i++) {
      const raw = binary.charCodeAt(i) & 0xff;
      out.push(raw > 0x7f ? raw - 0x100 : raw);
    }
  }
  return out;
}

function manualDecode(base64: string): string {
  const clean = base64.replace(/[^A-Za-z0-9+/]/g, "");
  let out = "";
  for (let i = 0; i < clean.length; i += 4) {
    const n = (B64.indexOf(clean[i]) << 18) | (B64.indexOf(clean[i + 1]) << 12) | ((B64.indexOf(clean[i + 2]) & 63) << 6) | (B64.indexOf(clean[i + 3]) & 63);
    out += String.fromCharCode((n >> 16) & 0xff);
    if (i + 2 < clean.length) out += String.fromCharCode((n >> 8) & 0xff);
    if (i + 3 < clean.length) out += String.fromCharCode(n & 0xff);
  }
  return out;
}
